import { Injectable, inject, signal } from '@angular/core';
import { dsApi, dsDtos, dsEnums } from '@vectronic/openApi-admin';
import { BatteryTypeListItem, BatteryTypeOverview } from '@vectronic/util';
import { isEqual } from 'es-toolkit';
import { deepEquals } from 'nx/src/utils/json-diff';
import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  map,
  mergeMap,
  shareReplay,
  take,
  tap,
} from 'rxjs';
import { DeviceTypeService } from './device-type.service';
import { HousingTypeService } from './housing-type.service';

@Injectable({ providedIn: 'root' })
export class BatteryTypeService {
  private batteryTypeApi = inject(dsApi.BatteryTypes);
  private housingTypeService = inject(HousingTypeService);
  private deviceTypeService = inject(DeviceTypeService);

  batteryTypes = signal<dsDtos.BatteryType[]>([]);

  constructor() {
    this.refresh().subscribe();
  }

  private _batteryTypes$ = new ReplaySubject<dsDtos.BatteryType[]>(1);

  batteryTypes$() {
    return this._batteryTypes$;
  }

  refresh() {
    return this.batteryTypeApi.getBatteryTypes().pipe(
      tap((batteryTypes) => {
        this._batteryTypes$.next(batteryTypes);
        this.batteryTypes.set(batteryTypes);
      }),
    );
  }

  add(batteryType: dsDtos.BatteryTypeInput) {
    return this.batteryTypeApi.addBatteryType(batteryType).pipe(mergeMap(() => this.refresh()));
  }

  edit(id: string, batteryType: dsDtos.BatteryTypeInput) {
    return this.batteryTypeApi
      .editBatteryType(id, batteryType)
      .pipe(mergeMap(() => this.refresh()));
  }

  delete(id: string) {
    return this.batteryTypeApi.deleteBatteryType(id).pipe(mergeMap(() => this.refresh()));
  }

  batteryTypeOverview$ = combineLatest({
    housingTypes: this.housingTypeService.housingTypes$(),
    batteryTypes: this.batteryTypes$(),
    deviceTypes: this.deviceTypeService.deviceTypes$(),
  }).pipe(
    map(({ housingTypes, batteryTypes, deviceTypes }) =>
      batteryTypes.map(
        (batteryType) =>
          ({
            ...batteryType,
            housingType: housingTypes.find(({ id }) => id === batteryType.housingTypeId),
            possibleDeviceTypes: batteryType.possibleDeviceTypeIds?.map((deviceTypeId) =>
              deviceTypes.find((deviceType) => deviceType.id === deviceTypeId),
            ),
          }) as BatteryTypeListItem,
      ),
    ),
    map((batteryTypes) => {
      // in the following sections, the battery types are grouped into overviews.
      // first, they are converted into an overview by converting (main, camera, DO) battery units into simple strings.
      // then, different overviews are joined by finding overviews with the same properties except for camera, DO, HP/SP.
      // step for step, they are joined.

      const step0: BatteryTypeOverview[] = [];
      batteryTypes.forEach((batteryType) => {
        const overviewTemplate = {
          batterySize: batteryType.batterySize,
          batteryShape: batteryType.batteryShape,
          isFix: batteryType.isFix,
          isDeprecated: batteryType.isDeprecated,
          camera: BatteryTypeService.getCameraString(batteryType.cameraBatteryUnits),
          dropOff: BatteryTypeService.getDropOffString(batteryType.dropOffBatteryUnits),
          hpSps: BatteryTypeService.getHpSpsString(batteryType.mainBatteryUnits),
          types: [],
          namePrefix: batteryType.namePrefix,
        } as BatteryTypeOverview;

        let overview: BatteryTypeOverview = step0.find((o) =>
          BatteryTypeService.equalsIgnoringFields(overviewTemplate, o, ['types']),
        );
        if (!overview) {
          overview = overviewTemplate;
          step0.push(overview);
        }
        overview.types.push(batteryType);
      });

      const step1Camera = [];
      for (const o of step0) {
        const alreadyJoined = step1Camera.find((_o) =>
          BatteryTypeService.equalsIgnoringFields(o, _o, ['types', 'camera', 'cameraCombinations']),
        );
        if (alreadyJoined) {
          continue;
        }
        const joinThem = step0.filter((_o) =>
          BatteryTypeService.equalsIgnoringFields(o, _o, ['types', 'camera']),
        );
        const joined = {
          batterySize: o.batterySize,
          batteryShape: o.batteryShape,
          isFix: o.isFix,
          isDeprecated: o.isDeprecated,
          cameraCombinations: joinThem
            .map(({ types }) => types)
            .flat()
            .map(({ cameraBatteryUnits }) => BatteryTypeService.getCameraString(cameraBatteryUnits))
            .filter((v, i, a) => i === a.indexOf(v))
            .sort()
            .reverse(),
          dropOff: o.dropOff,
          hpSps: o.hpSps,
          types: joinThem.map(({ types }) => types).flat(),
          namePrefix: o.namePrefix,
        };
        step1Camera.push(joined);
      }

      const step2DropOff = [];
      for (const o of step1Camera) {
        const alreadyJoined = step2DropOff.find((_o) =>
          BatteryTypeService.equalsIgnoringFields(o, _o, [
            'types',
            'dropOff',
            'dropOffCombinations',
          ]),
        );
        if (alreadyJoined) {
          continue;
        }
        const joinThem = step1Camera.filter((_o) =>
          BatteryTypeService.equalsIgnoringFields(o, _o, ['types', 'dropOff']),
        );
        const joined = {
          batterySize: o.batterySize,
          batteryShape: o.batteryShape,
          isFix: o.isFix,
          isDeprecated: o.isDeprecated,
          cameraCombinations: o.cameraCombinations,
          dropOffCombinations: joinThem
            .map(({ types }) => types)
            .flat()
            .map(({ dropOffBatteryUnits }) =>
              BatteryTypeService.getDropOffString(dropOffBatteryUnits),
            )
            .filter((v, i, a) => i === a.indexOf(v))
            .sort()
            .reverse(),
          hpSps: o.hpSps,
          types: joinThem.map(({ types }) => types).flat(),
          namePrefix: o.namePrefix,
        };
        step2DropOff.push(joined);
      }

      const step3HpSps = [];
      for (const o of step2DropOff) {
        const alreadyJoined = step3HpSps.find((_o) =>
          BatteryTypeService.equalsIgnoringFields(o, _o, ['types', 'hpSps', 'hpSpsCombinations']),
        );
        if (alreadyJoined) {
          continue;
        }
        const joinThem = step2DropOff.filter((_o) =>
          BatteryTypeService.equalsIgnoringFields(o, _o, ['types', 'hpSps']),
        );
        const joined = {
          batterySize: o.batterySize,
          batteryShape: o.batteryShape,
          isFix: o.isFix,
          isDeprecated: o.isDeprecated,
          cameraCombinations: o.cameraCombinations,
          dropOffCombinations: o.dropOffCombinations,
          hpSpsCombinations: joinThem
            .map(({ types }) => types)
            .flat()
            .map(({ mainBatteryUnits }) => BatteryTypeService.getHpSpsString(mainBatteryUnits))
            .filter((v, i, a) => i === a.indexOf(v))
            .sort()
            .reverse(),
          types: joinThem.map(({ types }) => types).flat(),
          namePrefix: o.namePrefix,
        };
        step3HpSps.push(joined);
      }

      return step3HpSps.sort((a, b) => {
        if (a.isDeprecated && !b.isDeprecated) {
          return 1;
        } else if (!a.isDeprecated && b.isDeprecated) {
          return -1;
        } else if (a.isDeprecated && b.isDeprecated) {
          return a.batterySize > b.batterySize ? 1 : -1;
        } else {
          return a.batterySize > b.batterySize ? 1 : -1;
        }
      });
    }),
    mergeMap((batteryTypeOverview) =>
      this.selectedOverview.pipe(
        take(1),
        map((selectedOverview) => {
          if (selectedOverview) {
            const newSelected = batteryTypeOverview.find(
              (o) =>
                o.batterySize === selectedOverview.batterySize &&
                o.batteryShape === selectedOverview.batteryShape &&
                o.isFix === selectedOverview.isFix &&
                o.isDeprecated === selectedOverview.isDeprecated &&
                deepEquals(o.cameraCombinations, selectedOverview.cameraCombinations) &&
                deepEquals(o.dropOffCombinations, selectedOverview.dropOffCombinations) &&
                deepEquals(o.hpSpsCombinations, selectedOverview.hpSpsCombinations) &&
                o.namePrefix === selectedOverview.namePrefix,
            );
            this.selectedOverview.next(newSelected);
          }
          return batteryTypeOverview;
        }),
      ),
    ),
    shareReplay(1),
  );

  selectedOverview = new BehaviorSubject<BatteryTypeOverview>(undefined);

  static getDropOffString(dropOffBatteryUnits: dsEnums.DropOffBatteryUnits[]) {
    return dropOffBatteryUnits && dropOffBatteryUnits.length > 0 ? 'YES' : 'NO';
  }

  static getHpSpsString(mainBatteryUnits: dsEnums.MainBatteryUnits[]) {
    if (mainBatteryUnits) {
      if (mainBatteryUnits.includes('HLC_PULSES_PLUS')) {
        return 'HP';
      }
      if (mainBatteryUnits.includes('LINCM14500')) {
        return 'SP';
      }
    }
    return 'NO';
  }

  static getCameraString(cameraBatteryUnits: dsEnums.CameraBatteryUnits[]) {
    return cameraBatteryUnits && cameraBatteryUnits.length > 0 ? 'YES' : 'NO';
  }

  getCameraBatteriesBySize(batterySize: dsEnums.BatterySize): dsDtos.BatteryType[] {
    const list = this.batteryTypes()?.filter((bt) => bt.batterySize === batterySize);
    if (!list) return [];

    const seen = new Set<string>();
    return list.filter((bt) => {
      if (!bt.cameraBatteryUnits?.length) return false;
      const key = (bt.cameraBatteryUnits ?? []).join(',');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private static equalsIgnoringFields<T>(o1: T, o2: T, ignoreFields: string[]) {
    for (const key in o1) {
      if (ignoreFields.indexOf(key) === -1 && !isEqual(o1[key], o2[key])) {
        return false;
      }
    }
    return true;
  }
}
